import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

import { mcmEndpointToName } from '@go-shared/lib/mcm';
import { mcmEndpointToIcon } from '@go-client/lib/mcm';
import { EventTemplate } from '@go-shared/types/eventTemplate';
import { Plan, VenueDisposition } from '@go-shared/types/plan';
import {
  CommonTextTranslation,
  ContentTemplateTranslation,
  EventTemplateTranslation,
  VenueTemplateTranslation,
} from '@go-shared/types/translation';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleInput } from '../BubbleUI';

import './DailySchedulePanel.scss';
import { Event } from '@go-shared/types/event';
import DailyEventListPanel from './DailyEventListPanel';
import DailyContentListPanel from './DailyContentListPanel';
import { BareContentTemplate } from '@go-shared/types/contentTemplate';
import { canEditCanvas } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { isDayLocked } from '@go-client/lib/sailing';
import { SailingDayBundle } from '@go-shared/types/sailing';
import { extractTemplateStatus } from '@go-client/lib/itinerary';
import { Content } from '@go-shared/types/content';
import { GroupEvent } from '@go-shared/types/groupEvent';
import { StaffTemplate } from '@go-shared/types/staffTemplate';
import DailyVenueListPanel from './DailyVenueListPanel';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { getHourFormat } from '@go-shared/lib/property';

export type DailySchedulePanelMode = 'default' | 'mcm' | 'focus';

interface DailySchedulePanelPropsBase {
  open: boolean;
  width: number;
  plan: Plan;
  groupEvts: GroupEvent[];
  eventTemplates: EventTemplate[];
  venueTemplates: VenueTemplate[];
  staffTemplates: StaffTemplate[];
  contentTemplates: BareContentTemplate[];
  eventTemplateTranslations: EventTemplateTranslation[];
  contentTemplateTranslations: ContentTemplateTranslation[];
  venueTemplateTranslations: VenueTemplateTranslation[];
  commonTextTranslations: CommonTextTranslation[];
  dayBundle?: SailingDayBundle;
  onToggle: (show: boolean) => void;
  onAddBtnClick: (activeTab: SchedulePanelTabs) => void;
  onUpdateEvent: (event: Event, applyStaffToPlan?: boolean, transferToTargetDay?: string) => void;
  onEditEvent: (event: Event) => void;
  onRemoveEvent: (eventId: string) => void;
  onUpdateContent: (content: Content, transferToTargetDay?: string) => void;
  onEditContent: (content: Content) => void;
  onRemoveContent: (contentId: string) => void;
  onUpdateVenue: (venue: VenueDisposition) => void;
}

type DailySchedulePanelProps = DailySchedulePanelPropsBase & React.HTMLProps<HTMLDivElement>;

type SchedulePanelTabs = 'events' | 'venueData' | 'content';

export interface DailySchedulePanelRef {
  toggle(tab: SchedulePanelTabs): void;
}

export default forwardRef<DailySchedulePanelRef, DailySchedulePanelProps>((props, ref) => {
  const {
    open,
    width,
    onToggle,
    plan,
    groupEvts,
    eventTemplates,
    venueTemplates,
    contentTemplates,
    staffTemplates,
    eventTemplateTranslations,
    contentTemplateTranslations,
    venueTemplateTranslations,
    commonTextTranslations,
    dayBundle,
    onAddBtnClick,
    onUpdateContent,
    onEditContent,
    onRemoveContent,
    onUpdateEvent,
    onEditEvent,
    onRemoveEvent,
    onUpdateVenue,
    className: propClasses,
    style: propStyles,
    ...rest
  } = props;

  let classes = ['DailySchedulePanel border-l flex flex-col bg-white'];
  if (open) classes.push('open');
  if (propClasses) classes.push(propClasses);

  let styles: React.CSSProperties = { width };
  if (propStyles) styles = { ...styles, ...propStyles };

  const serverInfo = useServerInfo();
  const { user } = useUserInfo();
  const { activeProperty: property } = useActiveProperty();

  const [search, setSearch] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [mode, setMode] = useState<DailySchedulePanelMode>('default');

  const [focusLang, setFocusLang] = useState<string | undefined>(undefined);
  const [focusEdp, setFocusEdp] = useState<string>(serverInfo.mcmEndpoints[0]);
  const [focusZone, setFocusZone] = useState<string | undefined>(property.zones[0]?._id);

  const [activeTab, setActiveTab] = useState<SchedulePanelTabs>('events');
  const [mcmVisibleEdps, setMcmVisibleEdps] = useState<{ [key: string]: boolean }>(
    serverInfo.mcmEndpoints.reduce((acc, x, i) => {
      const result = { ...acc, [x]: i <= 2 }; // only select first 3 by default
      return result;
    }, {})
  );

  const togglePanelBtn = (
    <BubbleButton
      variant={open ? 'white' : 'crimson'}
      inverse={open}
      className="h-full py-0-i rounded-0-i grid items-center justify-center"
      onClick={() => onToggle(!open)}
      icon={open ? 'xmark-large' : 'list'}
    />
  );

  const userCanEdit = canEditCanvas(user);
  const dayIsLocked = isDayLocked(dayBundle?.day.locked ?? false);
  const { templateStatus } = extractTemplateStatus(dayBundle?.day);

  const eventsLocked = !userCanEdit || dayIsLocked || templateStatus.events === 'linked';
  const venueLocked = !userCanEdit || dayIsLocked || templateStatus.venueData === 'linked';
  const contentLocked = !userCanEdit || dayIsLocked || templateStatus.content === 'linked';

  useImperativeHandle(ref, () => ({
    toggle: tab => {
      setActiveTab(tab);
    },
  }));

  const hourFormat = getHourFormat(props.plan.property);

  return (
    <div {...rest} className={classes.join(' ')} style={styles}>
      <div className="DailySchedulePanelList flex-shrink-0 flex items-stretch bg-crimson">
        {!open && <>{togglePanelBtn}</>}
        <span className="mx-3 font-bold uppercase text-white self-center">DAILY SCHEDULE</span>
        <div className="ml-auto"></div>
        {togglePanelBtn}
      </div>
      <div className="flex items-center">
        <BubbleButton
          block
          appearance="squared"
          variant={activeTab === 'events' ? 'navy' : 'smoke'}
          onClick={() => setActiveTab('events')}
        >
          Events
        </BubbleButton>
        <BubbleButton
          block
          appearance="squared"
          variant={activeTab === 'venueData' ? 'navy' : 'smoke'}
          onClick={() => setActiveTab('venueData')}
        >
          Venues
        </BubbleButton>
        <BubbleButton
          block
          appearance="squared"
          variant={activeTab === 'content' ? 'navy' : 'smoke'}
          onClick={() => setActiveTab('content')}
        >
          Content
        </BubbleButton>
      </div>
      <div className="flex-grow overflow-y-auto">
        <div className="sticky top-0" style={{ zIndex: 12 }}>
          <div className="flex items-stretch bg-white" style={{ height: 37 }}>
            {activeTab !== 'venueData' && (
              <BubbleButton
                variant="lime"
                appearance="squared"
                icon="plus"
                disabled={
                  (activeTab === 'events' && eventsLocked) ||
                  (activeTab === 'content' && contentLocked)
                }
                onClick={() => onAddBtnClick(activeTab)}
              />
            )}
            <BubbleInput
              type="text"
              className="flex-grow"
              block
              appearance="bare"
              inputClassName="px-3 py-2 border-b"
              value={search}
              onValue={setSearch}
              placeholder="Search..."
            />
            <BubbleButton
              variant="ocean"
              appearance="squared"
              icon="filter"
              onClick={() => {
                setShowSettings(state => !state);
              }}
            />
          </div>
          {showSettings && (
            <div className="bg-eggshell border-b border-t p-3 pb-5 grid gap-4 shadow-lg">
              <BubbleFormGroup label="Mode" className="-mb-2">
                <div className="inline-flex items-center mr-3 mb-2 relative">
                  <BubbleInput
                    type="checkbox"
                    onValue={() => setMode('default')}
                    checked={mode === 'default'}
                    appearance="rounded"
                    inputClassName="stretched-link"
                  />
                  <span className="ml-1">Default</span>
                </div>
                <div className="inline-flex items-center mr-3 mb-2 relative">
                  <BubbleInput
                    type="checkbox"
                    onValue={() => setMode('mcm')}
                    checked={mode === 'mcm'}
                    appearance="rounded"
                    inputClassName="stretched-link"
                  />
                  <span className="ml-1">MCM</span>
                </div>
                <div className="inline-flex items-center mr-3 mb-2 relative">
                  <BubbleInput
                    type="checkbox"
                    onValue={() => setMode('focus')}
                    checked={mode === 'focus'}
                    appearance="rounded"
                    inputClassName="stretched-link"
                  />
                  <span className="ml-1">Preview*</span>
                </div>
              </BubbleFormGroup>
              {mode === 'mcm' && (
                <BubbleFormGroup label="Endpoints" className="-mb-2">
                  {serverInfo.mcmEndpoints.map(edp => (
                    <div className="inline-flex items-center mr-3 mb-2 relative" key={edp}>
                      <BubbleInput
                        type="checkbox"
                        checked={mcmVisibleEdps[edp]}
                        inputClassName="stretched-link"
                        onValue={chk => setMcmVisibleEdps({ ...mcmVisibleEdps, [edp]: chk })}
                      />
                      <FontAwesomeIcon
                        className="ml-1"
                        fixedWidth
                        icon={mcmEndpointToIcon(edp, true)}
                      />
                      <span className="ml-1">{mcmEndpointToName(edp)}</span>
                    </div>
                  ))}
                </BubbleFormGroup>
              )}
              {mode === 'focus' && (
                <>
                  <BubbleFormGroup label="Endpoint" className="-mb-2">
                    {serverInfo.mcmEndpoints.map(edp => (
                      <div className="inline-flex items-center mr-3 mb-2 relative" key={edp}>
                        <BubbleInput
                          type="checkbox"
                          checked={focusEdp === edp}
                          onValue={() => setFocusEdp(edp)}
                          inputClassName="stretched-link"
                          appearance="rounded"
                        />
                        <FontAwesomeIcon
                          className="ml-1"
                          fixedWidth
                          icon={mcmEndpointToIcon(edp, true)}
                        />
                        <span className="ml-1">{mcmEndpointToName(edp)}</span>
                      </div>
                    ))}
                  </BubbleFormGroup>
                  {property.zones.length > 0 && (
                    <BubbleFormGroup label="Audience" className="-mb-2">
                      {property.zones.map(zone => (
                        <div className="inline-flex items-center mr-3 mb-2 relative" key={zone._id}>
                          <BubbleInput
                            type="checkbox"
                            checked={focusZone === zone._id}
                            onValue={() => setFocusZone(zone._id)}
                            inputClassName="stretched-link"
                            appearance="rounded"
                          />
                          <span className="ml-1">{zone.name}</span>
                        </div>
                      ))}
                    </BubbleFormGroup>
                  )}
                  <BubbleFormGroup label="Language" className="-mb-2">
                    <div
                      className="inline-flex items-center mr-3 mb-2 relative"
                      key={serverInfo.defaultLanguage.key}
                    >
                      <BubbleInput
                        type="checkbox"
                        checked={focusLang === undefined}
                        onValue={() => setFocusLang(undefined)}
                        inputClassName="stretched-link"
                        appearance="rounded"
                      />
                      <span className="ml-1">{serverInfo.defaultLanguage.englishLabel}</span>
                    </div>
                    {serverInfo.languages.map(lang => (
                      <div className="inline-flex items-center mr-3 mb-2 relative" key={lang.key}>
                        <BubbleInput
                          type="checkbox"
                          checked={focusLang === lang.key}
                          onValue={() => setFocusLang(lang.key)}
                          inputClassName="stretched-link"
                          appearance="rounded"
                        />
                        <span className="ml-1">{lang.englishLabel}</span>
                      </div>
                    ))}
                  </BubbleFormGroup>
                </>
              )}
              {mode === 'focus' && (
                <div className="text-xs text-slate italic">
                  *Preview mode applies merged variables, translations, and all other overrides as
                  seen by guests. Use this to get better insights to what will be hidden and why.
                </div>
              )}
            </div>
          )}
        </div>
        {activeTab === 'events' ? (
          <DailyEventListPanel
            mode={mode}
            width={width}
            locked={eventsLocked}
            search={search}
            focusEdp={focusEdp}
            focusLang={focusLang}
            focusZone={focusZone}
            mcmVisibleEdps={serverInfo.mcmEndpoints.filter(edp => mcmVisibleEdps[edp])}
            plan={plan}
            hourFormat={hourFormat}
            groupEvts={groupEvts}
            eventTemplates={eventTemplates}
            venueTemplates={venueTemplates}
            staffTemplates={staffTemplates}
            eventTemplateTranslations={eventTemplateTranslations}
            commonTextTranslations={commonTextTranslations}
            onUpdateEvent={onUpdateEvent}
            onDeleteEvent={onRemoveEvent}
            onEditEvent={onEditEvent}
            dayBundle={dayBundle}
          />
        ) : activeTab === 'content' ? (
          <DailyContentListPanel
            mode={mode}
            locked={contentLocked}
            search={search}
            focusEdp={focusEdp}
            focusLang={focusLang}
            focusZone={focusZone}
            mcmVisibleEdps={serverInfo.mcmEndpoints.filter(edp => mcmVisibleEdps[edp])}
            plan={plan}
            hourFormat={hourFormat}
            venueTemplates={venueTemplates}
            contentTemplates={contentTemplates}
            contentTemplateTranslations={contentTemplateTranslations}
            onDeleteContent={onRemoveContent}
            onUpdateContent={onUpdateContent}
            onEditContent={onEditContent}
            dayBundle={dayBundle}
          />
        ) : activeTab === 'venueData' ? (
          <DailyVenueListPanel
            mode={mode}
            locked={venueLocked}
            search={search}
            focusEdp={focusEdp}
            focusLang={focusLang}
            focusZone={focusZone}
            mcmVisibleEdps={serverInfo.mcmEndpoints.filter(edp => mcmVisibleEdps[edp])}
            plan={plan}
            hourFormat={hourFormat}
            venueTemplates={venueTemplates}
            venueTemplateTranslations={venueTemplateTranslations}
            commonTextTranslations={commonTextTranslations}
            onUpdateVenue={onUpdateVenue}
          />
        ) : (
          <>Can't resolve daily schedule list component</>
        )}
      </div>
    </div>
  );
});
